export enum ProdBuildState {
  ALL,
  ALL_NO_LIST,
  JOINING_ONLY_GLOBAL_ENQUIRIES,
  JOINING_ONLY_NO_LIST,
  CRM_TEST,
  NO_JOIN_ONLINE,
  ENQUIRY_TOUR_ONLY,
  ENQUIRY_TOUR_JOIN_ONLY,
  TOUR_JOIN_ONLY_GLOBAL_ENQUIRIES,
  NONE_GLOBAL_ENQUIRIES,
  NONE,
}

type ProdBuildStateOptions = {
  shouldBuildPage: (page: string) => boolean;
  shouldIncludeInLists: boolean;
  shouldIncludeInOnlineJoiningLists: boolean;
  shouldIncludeInEnquiryForms: boolean;
  shouldAllowPageIndex: boolean;
  shouldNavigateViaNginxRedirectsWhenClosingWizard: boolean;
};

const options: Record<ProdBuildState, ProdBuildStateOptions> = {
  [ProdBuildState.ALL]: {
    shouldBuildPage: () => true,
    shouldIncludeInLists: true,
    shouldIncludeInOnlineJoiningLists: true,
    shouldIncludeInEnquiryForms: true,
    shouldAllowPageIndex: true,
    shouldNavigateViaNginxRedirectsWhenClosingWizard: false,
  },
  [ProdBuildState.ALL_NO_LIST]: {
    shouldBuildPage: () => true,
    shouldIncludeInLists: false,
    shouldIncludeInOnlineJoiningLists: false,
    shouldIncludeInEnquiryForms: false,
    shouldAllowPageIndex: false,
    shouldNavigateViaNginxRedirectsWhenClosingWizard: false,
  },
  [ProdBuildState.JOINING_ONLY_GLOBAL_ENQUIRIES]: {
    shouldBuildPage: (page) => page.includes('join-online'),
    shouldIncludeInLists: false,
    shouldIncludeInOnlineJoiningLists: false,
    shouldIncludeInEnquiryForms: true,
    shouldAllowPageIndex: false,
    shouldNavigateViaNginxRedirectsWhenClosingWizard: true,
  },
  [ProdBuildState.JOINING_ONLY_NO_LIST]: {
    shouldBuildPage: (page) => page.includes('join-online'),
    shouldIncludeInLists: false,
    shouldIncludeInOnlineJoiningLists: false,
    shouldIncludeInEnquiryForms: false,
    shouldAllowPageIndex: false,
    shouldNavigateViaNginxRedirectsWhenClosingWizard: true,
  },
  [ProdBuildState.CRM_TEST]: {
    shouldBuildPage: (page) =>
      page.includes('join-online') ||
      page.includes('enquire-now') ||
      page.includes('enquiry-form') ||
      page.includes('book-a-tour') ||
      page.includes('book-a-club-welcome') ||
      page.includes('manage-your-tour') ||
      page.includes('membership-trial'),
    shouldIncludeInLists: false,
    shouldIncludeInOnlineJoiningLists: false,
    shouldIncludeInEnquiryForms: false,
    shouldAllowPageIndex: false,
    shouldNavigateViaNginxRedirectsWhenClosingWizard: true,
  },
  [ProdBuildState.NO_JOIN_ONLINE]: {
    shouldBuildPage: (page) => !page.includes('join-online'),
    shouldIncludeInLists: true,
    shouldIncludeInOnlineJoiningLists: false,
    shouldIncludeInEnquiryForms: true,
    shouldAllowPageIndex: true,
    shouldNavigateViaNginxRedirectsWhenClosingWizard: false,
  },
  [ProdBuildState.ENQUIRY_TOUR_ONLY]: {
    shouldBuildPage: (page) =>
      page.includes('enquire-now') ||
      page.includes('enquiry-form') ||
      page.includes('book-a-tour') ||
      page.includes('manage-your-tour'),
    shouldIncludeInLists: false,
    shouldIncludeInOnlineJoiningLists: false,
    shouldIncludeInEnquiryForms: true,
    shouldAllowPageIndex: true,
    shouldNavigateViaNginxRedirectsWhenClosingWizard: true,
  },
  [ProdBuildState.ENQUIRY_TOUR_JOIN_ONLY]: {
    shouldBuildPage: (page) =>
      page.includes('join-online') ||
      page.includes('enquire-now') ||
      page.includes('enquiry-form') ||
      page.includes('book-a-tour') ||
      page.includes('book-a-club-welcome') ||
      page.includes('manage-your-tour'),
    shouldIncludeInLists: false,
    shouldIncludeInOnlineJoiningLists: true,
    shouldIncludeInEnquiryForms: true,
    shouldAllowPageIndex: true,
    shouldNavigateViaNginxRedirectsWhenClosingWizard: true,
  },
  [ProdBuildState.TOUR_JOIN_ONLY_GLOBAL_ENQUIRIES]: {
    shouldBuildPage: (page) =>
      page.includes('join-online') ||
      page.includes('book-a-tour') ||
      page.includes('book-a-club-welcome') ||
      page.includes('manage-your-tour'),
    shouldIncludeInLists: false,
    shouldIncludeInOnlineJoiningLists: false,
    shouldIncludeInEnquiryForms: true,
    shouldAllowPageIndex: false,
    shouldNavigateViaNginxRedirectsWhenClosingWizard: true,
  },
  [ProdBuildState.NONE_GLOBAL_ENQUIRIES]: {
    shouldBuildPage: () => false,
    shouldIncludeInLists: false,
    shouldIncludeInOnlineJoiningLists: false,
    shouldIncludeInEnquiryForms: true,
    shouldAllowPageIndex: false,
    shouldNavigateViaNginxRedirectsWhenClosingWizard: true,
  },
  [ProdBuildState.NONE]: {
    shouldBuildPage: () => false,
    shouldIncludeInLists: false,
    shouldIncludeInOnlineJoiningLists: false,
    shouldIncludeInEnquiryForms: false,
    shouldAllowPageIndex: false,
    shouldNavigateViaNginxRedirectsWhenClosingWizard: true,
  },
};

export const shouldBuildPageForProdBuildState = (page: string, prodBuildState: ProdBuildState) =>
  options[prodBuildState].shouldBuildPage(page);

export const shouldBuildClubPageForProdBuildState = (prodBuildState: ProdBuildState) =>
  shouldBuildPageForProdBuildState('/clubs', prodBuildState);

export const shouldIncludeInLists = (prodBuildState: ProdBuildState) =>
  options[prodBuildState].shouldIncludeInLists;

export const shouldIncludeInOnlineJoiningLists = (prodBuildState: ProdBuildState) =>
  options[prodBuildState].shouldIncludeInOnlineJoiningLists;

export const shouldIncludeInEnquiryForms = (prodBuildState: ProdBuildState) =>
  options[prodBuildState].shouldIncludeInEnquiryForms;

export const shouldAllowPageIndexForClub = (prodBuildState: ProdBuildState) =>
  options[prodBuildState].shouldAllowPageIndex;

export const shouldUseNginxRedirectsWhenClosingWizard = (prodBuildState: ProdBuildState) =>
  options[prodBuildState].shouldNavigateViaNginxRedirectsWhenClosingWizard;
